import { useCallback, useEffect, useMemo, useState } from 'react'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { type SecondaryCategoryConfig } from '@webapp/platform/organization-management/entities/SecondaryCategoryConfig'
import messages from './messages'
import { SECONDARY_CATEGORY_LIST_PAGE_SIZE } from '../../constants'
import { useQueryReportAccessSecondaryCategories } from '../../models/useQueryReportAccessSecondaryCategories'
import { type SecondaryCategoryRowViewModel } from '../../types'

const SEARCH_DEBOUNCE_MS = 400

export interface IUseReportAccessSecondaryCategoryListPresenterParams {
  primaryCategoryId: number
  page: number
  selectedSecondaryCategoryId: number | undefined
  isAllSecondaryCategoriesSelected: boolean
  onSelect: (config: SecondaryCategoryConfig) => void
  onToggleSelectAll: () => void
  onExitSelectAll: () => void
  onPageChange: (page: number) => void
}

export const useReportAccessSecondaryCategoryListPresenter = ({
  primaryCategoryId,
  page,
  selectedSecondaryCategoryId,
  isAllSecondaryCategoriesSelected,
  onSelect,
  onToggleSelectAll,
  onExitSelectAll,
  onPageChange,
}: IUseReportAccessSecondaryCategoryListPresenterParams) => {
  const intl = useProcurifyIntl()

  const [searchTerm, setSearchTerm] = useState('')
  // The page resets with the debounced term, not per keystroke: until the
  // consumer's page catches up, the query reads page 1 so no request goes out
  // for the new term on a stale page or for page 1 of the old term
  const [appliedSearch, setAppliedSearch] = useState({
    term: '',
    isPageResetPending: false,
  })

  useEffect(() => {
    const timer = setTimeout(() => {
      const term = searchTerm.trim()
      setAppliedSearch((applied) =>
        applied.term === term ? applied : { term, isPageResetPending: true }
      )
    }, SEARCH_DEBOUNCE_MS)

    return () => clearTimeout(timer)
  }, [searchTerm])

  useEffect(() => {
    if (!appliedSearch.isPageResetPending) return

    onPageChange(1)
    setAppliedSearch((applied) => ({ ...applied, isPageResetPending: false }))
  }, [appliedSearch.isPageResetPending, onPageChange])

  const {
    secondaryCategoryConfigs,
    pagination,
    isFetching,
    isPreviousData,
    error,
    refetch,
  } = useQueryReportAccessSecondaryCategories(primaryCategoryId, {
    page: appliedSearch.isPageResetPending ? 1 : page,
    pageSize: SECONDARY_CATEGORY_LIST_PAGE_SIZE,
    search: appliedSearch.term || undefined,
  })

  useEffect(() => {
    if (error) {
      toast.error({
        title: intl.formatMessage(messages.fetchErrorToastTitle),
        children:
          getMessageFromError(error) ??
          intl.formatMessage(messages.fetchErrorToastMessage),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  const activeSecondaryCategoryId = isAllSecondaryCategoriesSelected
    ? undefined
    : selectedSecondaryCategoryId

  const rows = useMemo<SecondaryCategoryRowViewModel[]>(
    () =>
      secondaryCategoryConfigs.map((config) => ({
        id: config.id,
        name: config.name,
        isActive: config.id === activeSecondaryCategoryId,
      })),
    [secondaryCategoryConfigs, activeSecondaryCategoryId]
  )

  const handleSearchChange = useCallback(
    (term: string) => {
      setSearchTerm(term)

      // Searching narrows the list, so "all" no longer describes what the
      // user sees; the select-all bar is hidden while searching (BHV-009)
      if (isAllSecondaryCategoriesSelected) onExitSelectAll()
    },
    [isAllSecondaryCategoriesSelected, onExitSelectAll]
  )

  const handleRowClick = useCallback(
    (secondaryCategoryId: number) => {
      // Re-clicking the active row is a no-op (BHV-008)
      if (secondaryCategoryId === activeSecondaryCategoryId) return

      const config = secondaryCategoryConfigs.find(
        ({ id }) => id === secondaryCategoryId
      )

      if (config) onSelect(config)
    },
    [activeSecondaryCategoryId, secondaryCategoryConfigs, onSelect]
  )

  const handleRetry = useCallback(() => refetch(), [refetch])

  const isEmpty = secondaryCategoryConfigs.length === 0
  const isSearching = searchTerm.trim().length > 0
  // A failed load must never read as "no Departments"; kept previous-page
  // data belongs to another request, so it doesn't count as a result either
  const showErrorState =
    Boolean(error) && !isFetching && (isEmpty || isPreviousData)

  return {
    data: {
      rows,
      currentPage: pagination?.page || page,
      numPages: pagination?.numPages || 1,
      searchTerm,
    },
    states: {
      showInitialLoader: isEmpty && isFetching,
      showErrorState,
      showEmptyState: isEmpty && !isFetching && !showErrorState,
      showEmptyStateContent: !isSearching,
      showList: !isEmpty && !showErrorState,
      isRefetching: !isEmpty && isFetching,
      // Hide the "Select all" bar while a search term is present (BHV-009)
      isSelectAllBarVisible: !isSearching,
    },
    methods: {
      onSearchChange: handleSearchChange,
      onPageChange,
      onRowClick: handleRowClick,
      onToggleSelectAll,
      onRetry: handleRetry,
    },
  }
}
